import {
  Search,
  ChevronDown,
  Shield,
  Building2,
  LogOut,
  Crown,
  Headphones,
  User as UserIcon,
  Menu,
  ExternalLink,
  KeyRound,
} from "lucide-react";
import { openPtsApp } from "@sd/lib/openPtsApp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { sd, SD_BASE } from "@sd/lib/sdPaths";
import { isExternalSdRole } from "@sd/lib/sdRoles";
import { useCurrentUserName } from "@sd/hooks/useCurrentUserName";
import { NotificationBell } from "@sd/components/notifications/NotificationBell";
import { ResetPasswordDialog } from "@sd/components/common/ResetPasswordDialog";

interface HeaderProps {
  onSearchClick?: () => void;
  /** Hide center global search (e.g. agent workbench has its own filter row). ⌘K still opens Command Palette. */
  hideGlobalSearch?: boolean;
  /** Mobile-only — opens nav drawer; hidden on md+ screens. */
  onMobileMenuClick?: () => void;
}

const roleMeta: Record<string, { label: string; icon: typeof Crown }> = {
  ServiceDesk_Admin: { label: "Admin", icon: Crown },
  ServiceDesk_SuperAgent: { label: "Super Agent", icon: Shield },
  ServiceDesk_Agent: { label: "Agent", icon: Headphones },
  ServiceDesk_External_Agent: { label: "External Agent", icon: Headphones },
  ServiceDesk_External_Customer: { label: "External Customer", icon: UserIcon },
  ServiceDesk_User: { label: "End User", icon: UserIcon },
};

export function Header({
  onSearchClick,
  hideGlobalSearch = false,
  onMobileMenuClick,
}: HeaderProps) {
  const navigate = useNavigate();
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const { role, isSuperAdmin, isSupportAgent, isEndUser, signOut } = useAuth();

  const meta = role ? roleMeta[role] : null;
  const RoleIcon = meta?.icon;

  const { displayName: agentName, initials } = useCurrentUserName();

  const handleLogout = async () => {
    await signOut();
    navigate(sd("login"), { replace: true });
  };

  return (
    <>
    <header className="flex h-14 items-center justify-between border-b-2 border-border bg-card px-4">
      {/* Logo */}
      <div className="flex min-w-0 items-center gap-2">
        {onMobileMenuClick ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 lg:hidden"
            onClick={onMobileMenuClick}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        ) : null}
        <div
          className="flex min-w-0 cursor-pointer items-center gap-2"
          onClick={() => navigate(`${SD_BASE}/`)}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            SD
          </div>
          <span className="font-semibold text-foreground">ServiceDesk</span>
          {meta && (
            <Badge
              variant="secondary"
              className="ml-2 hidden gap-1 sm:inline-flex"
            >
              {RoleIcon && <RoleIcon className="h-3 w-3" />}
              {meta.label}
            </Badge>
          )}
        </div>
      </div>

      {!hideGlobalSearch ? (
        <div className="mx-4 max-w-lg flex-1">
          <button
            type="button"
            onClick={onSearchClick}
            className="relative w-full"
          >
            <div className="relative flex items-center">
              <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets, assets..."
                className="h-9 cursor-pointer border-border bg-secondary pl-10 pr-12"
                readOnly
              />
              <kbd className="pointer-events-none absolute right-3 inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                ⌘K
              </kbd>
            </div>
          </button>
        </div>
      ) : (
        <div className="flex-1" aria-hidden />
      )}

      {/* Right Actions */}
      <div className="flex items-center gap-3">
        {isSuperAdmin && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(sd("admin"))}
            title="Admin Panel"
          >
            <Shield className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}
        {isSuperAdmin && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(sd("customer"))}
            title="Customer Portal"
          >
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}
        {isSupportAgent && !isExternalSdRole(role) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openPtsApp({ sameTab: false })}
            title="Open PTS"
            className="hidden items-center gap-1.5 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 sm:flex"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold">Open PTS</span>
          </Button>
        )}
        {(isSupportAgent || isSuperAdmin) && <NotificationBell />}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-9 items-center gap-2 px-2"
            >
              <Avatar className="h-7 w-7 bg-primary text-primary-foreground">
                <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden flex-col items-start md:flex">
                {agentName && (
                  <span className="text-xs font-semibold leading-tight text-foreground">
                    {agentName}
                  </span>
                )}
                <span
                  className={`leading-tight text-foreground ${agentName ? "text-[10px] text-muted-foreground" : "text-sm"}`}
                >
                  {meta?.label || "Guest"}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="z-50 w-56 border-border bg-popover"
          >
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              {agentName && (
                <span className="font-semibold text-foreground">
                  {agentName}
                </span>
              )}
              <span
                className={
                  agentName ? "text-xs font-normal text-muted-foreground" : ""
                }
              >
                {meta?.label || "No role"}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isExternalSdRole(role) ? (
              <DropdownMenuItem
                onSelect={event => {
                  event.preventDefault();
                  window.setTimeout(() => setResetPasswordOpen(true), 0);
                }}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={() => {
                void handleLogout();
              }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    {isExternalSdRole(role) ? (
      <ResetPasswordDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
      />
    ) : null}
    </>
  );
}
